/*
 * ViewingAndSelectionScreen
 *
 * Single screen for viewing and selection modes so the grid stays mounted
 * and preserves filter/sort when switching between them.
 * Edit mode uses a separate screen with a snapshot of currently displayed rows.
 */

import React, { useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Row, Col, Button } from 'reactstrap';
import type { GridApi } from 'ag-grid-community';
import {
  SCREEN_MODES,
  TABS,
  TABS_PATHS,
  ADMIN_CSV_TIMESTAMP_COLUMNS,
} from '../constants';
import type { Tab } from '../constants';
import { getCsvProcessCellCallback } from '../utils';
import {
  addImportedRows,
  clearLoadedTabs,
  loadGroups,
  loadMappingData,
  saveMappingData,
  selectIsAnyLoading,
  setEditModeSnapshot,
  setScreenMode,
  clearSelectedRows,
} from '../../../features/mappingAdmin/mappingAdminSlice';
import type { EditModeSnapshot } from '../../../common/domain/mappingAdmin';
import {
  FieldMappingDataRow,
  FixingProxyMappingDataRow,
  LegalEntityMappingDataRow,
  MappingAdminState,
} from '../../../common/domain/mappingAdmin';
import MappingGridContainer from './MappingGridContainer';
import ImportModal from './ImportModal';
import { ViewingAndSelectionScreenProps } from '../interfaces';

function getDisplayedRowIds(api: GridApi): string[] {
  const ids: string[] = [];
  const count = api.getDisplayedRowCount();
  for (let i = 0; i < count; i++) {
    const node = api.getDisplayedRowAtIndex(i);
    if (node?.data?.id) ids.push(node.data.id);
  }
  return ids;
}

const ViewingAndSelectionScreen = ({
  username,
  dispatch,
}: ViewingAndSelectionScreenProps) => {
  const gridApiByTabRef = useRef<Partial<Record<Tab, GridApi>>>({});

  const { activeTab, screenMode, selectedRows } = useSelector(
    (state: any) => state.mappingAdmin as MappingAdminState,
  );
  const isAnyLoading = useSelector(selectIsAnyLoading);

  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const selectedRowsData = Object.values(selectedRows[activeTab]) as
    | FieldMappingDataRow[]
    | FixingProxyMappingDataRow[]
    | LegalEntityMappingDataRow[];
  const selectedRowsCount = selectedRowsData.length;

  const onEdit = () => {
    const api = gridApiByTabRef.current[activeTab];
    const snapshot: EditModeSnapshot = {};
    if (api) {
      snapshot[activeTab] = getDisplayedRowIds(api);
    }
    dispatch(setEditModeSnapshot({ snapshot }));
    dispatch(setScreenMode(SCREEN_MODES.EDITING_MODE));
  };

  const onImport = () => {
    dispatch(loadGroups());
    setIsImportModalOpen(true);
  };

  const handleImportModalClose = () => {
    setIsImportModalOpen(false);
  };

  const handleImportData = async (data: any[]) => {
    dispatch(addImportedRows({ rows: data, username }));
    await dispatch(saveMappingData(activeTab));
    dispatch(clearLoadedTabs());
    dispatch(loadMappingData(activeTab));
  };

  const onExport = () => {
    dispatch(setScreenMode(SCREEN_MODES.SELECTING_MODE));
  };

  const getCsvFilename = () => {
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const exportType = TABS_PATHS[activeTab];
    return `mapping-admin-${exportType}-export-${timestamp}.csv`;
  };

  const onGridReady = useCallback((tabType: Tab, api: GridApi) => {
    gridApiByTabRef.current[tabType] = api;
  }, []);

  const onBack = () => {
    dispatch(
      clearSelectedRows({ tabTypes: [TABS.RATES_FIELD, TABS.RATES_FIXING] }),
    );
    dispatch(setScreenMode(SCREEN_MODES.VIEWING_MODE));
  };

  const onExportSelected = () => {
    const api = gridApiByTabRef.current[activeTab];
    if (selectedRowsCount === 0 || !api) return;
    api.exportDataAsCsv({
      onlySelected: true,
      fileName: getCsvFilename(),
      processCellCallback: getCsvProcessCellCallback(
        ADMIN_CSV_TIMESTAMP_COLUMNS,
      ),
    });
    onBack();
  };

  const isViewing = screenMode === SCREEN_MODES.VIEWING_MODE;
  const isSelecting = screenMode === SCREEN_MODES.SELECTING_MODE;

  return (
    <>
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            {isViewing && (
              <>
                <div>
                  <Button
                    color="primary"
                    size="sm"
                    className="mr-2"
                    onClick={onEdit}
                  >
                    Edit...
                  </Button>
                </div>
                <div>
                  <Button
                    color="secondary"
                    size="sm"
                    className="mr-2"
                    onClick={onImport}
                    disabled={isAnyLoading}
                  >
                    Import from file
                  </Button>
                  <Button color="primary" size="sm" onClick={onExport}>
                    Export...
                  </Button>
                </div>
              </>
            )}
            {isSelecting && (
              <>
                <span className="text-muted">
                  {selectedRowsCount} row(s) selected
                </span>
                <div className="d-flex">
                  <Button
                    color="secondary"
                    size="sm"
                    className="mr-2"
                    onClick={onBack}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    size="sm"
                    disabled={selectedRowsCount === 0}
                    onClick={onExportSelected}
                  >
                    Export selected
                  </Button>
                </div>
              </>
            )}
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <MappingGridContainer
            username={username}
            dispatch={dispatch}
            onGridReady={onGridReady}
          />
        </Col>
      </Row>
      <ImportModal
        isOpen={isImportModalOpen}
        onClose={handleImportModalClose}
        onImport={handleImportData}
      />
    </>
  );
};

export default ViewingAndSelectionScreen;
